import React, { useState, useEffect } from "react";
import { C, TABLE } from "../constants";
import { parsePlanTaskLines as parseTaskLines } from "../utils/planTaskLines";
import { formatTime12 } from "./FormControls";

const getRowStyle = (activity = "") => {
  const text = activity.toLowerCase();
  if (text.includes("learning") || text.includes("📖")) return { bg: C.primarySoft, accent: C.primary, dot: C.primary, group: false };
  if (text.includes("project") || text.includes("implement") || text.includes("💻")) return { bg: C.successBg, accent: C.success, dot: C.success, group: false };
  if (text.includes("dsa") || text.includes("🧮")) return { bg: C.warningBg, accent: C.primaryDark, dot: C.primary, group: false };
  if (text.includes("placement") || text.includes("🎯")) return { bg: C.primarySoft, accent: C.primaryDark, dot: C.primary, group: false };
  if (text.includes("break") || text.includes("☕")) return { bg: "#F3F4F6", accent: "#6B7280", dot: "#6B7280", group: false };
  if (text.includes("lunch") || text.includes("🍽")) return { bg: C.primarySoft, accent: C.primary, dot: C.primary, group: false };
  if (text.includes("retro") || text.includes("🔄") || text.includes("retrospective")) return { bg: C.successBg, accent: C.success, dot: C.success, group: true };
  if (text.includes("standup") || text.includes("stand-up") || text.includes("kickoff") || text.includes("🤝")) return { bg: C.successBg, accent: C.success, dot: C.success, group: true };
  if (text.includes("group work") || text.includes("👥")) return { bg: C.warningBg, accent: C.primaryDark, dot: C.primary, group: true };
  return { bg: "#FFFFFF", accent: "#374151", dot: "#9CA3AF", group: false };
};

// DAY_NAMES used as fallback when real dayName is not available
const DAY_NAMES = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
const memberColors = ["#4F46E5", "#059669", "#1B4F72", "#DC2626", "#0891B2"];

/**
 * Resolves the display name for a day entry.
 * Prefers the real `dayName` from the plan (e.g. "Monday · Week 2")
 * over the computed fallback. Strips any leading "Week N - " prefix so
 * we don't render "Week 1 - Monday" as the tab label.
 */
function resolveDayLabel(d) {
  if (d.dayName && String(d.dayName).trim()) {
    // Remove "Week N - " / "W1 - " prefix if present
    return String(d.dayName).replace(/^W(?:eek\s*)?\d+\s*[-–]\s*/i, "").trim();
  }
  // Fallback: compute from day number
  return DAY_NAMES[(d.day - 1) % 5];
}

/**
 * Renders a full member's schedule using the SAME table format as the main
 * daily/weekly schedule (DailyPlanTable): Week tabs → Day tabs → timed
 * Time/Activity/Task table with checkboxes.
 *
 * @param {object[]} schedule - teamSchedule.schedule
 * @param {object[]} members  - teamSchedule.members
 */
export default function TeamDayTable({ schedule, members }) {
  const isSolo = (members?.length || 0) === 1;
  const [activeMemberId, setActiveMemberId] = useState(members?.[0]?.id || null);
  const [activeWeek, setActiveWeek] = useState(null);
  const [activeDayNum, setActiveDayNum] = useState(null);
  const [doneRows, setDoneRows] = useState(new Set());
  const [subTaskDone, setSubTaskDone] = useState(new Set());

  // Resolved *at render time* — if the selected member id doesn't exist in
  // the current `members` list (e.g. right after a re-sync swaps in a new
  // schedule/member set), fall back to the first member immediately instead
  // of waiting for the effect below to catch up on the next render. This is
  // what previously crashed: memberDays/daysThisWeek would compute as empty
  // for one render, and getDayKey(undefined) threw.
  const resolvedActiveMemberId =
    members?.some(m => m.id === activeMemberId) ? activeMemberId : (members?.[0]?.id ?? null);

  useEffect(() => {
    if (members?.length && !members.some((m) => m.id === activeMemberId)) {
      setActiveMemberId(members[0].id);
    }
  }, [members, activeMemberId]);

  const memberDays = (schedule || [])
    .map(d => ({ d, a: (d.assignments || []).find(a => a.memberId === resolvedActiveMemberId) }))
    .filter(x => x.a);

  const weeks = [...new Set(memberDays.map(x => x.d.week))].sort((a, b) => a - b);
  const currentWeek = weeks.includes(activeWeek) ? activeWeek : weeks[0];
  const daysThisWeek = memberDays.filter(x => x.d.week === currentWeek);

  // Use dayId when available (rows-based path), else fall back to day number.
  // Null-safe: `d` can legitimately be undefined when daysThisWeek is empty
  // (e.g. no schedule data yet for this member/week).
  const getDayKey = (d) => (d ? (d.dayId ?? d.day) : null);
  const currentDayKey = daysThisWeek.some(x => getDayKey(x.d) === activeDayNum)
    ? activeDayNum
    : getDayKey(daysThisWeek[0]?.d);
  const current = daysThisWeek.find(x => getDayKey(x.d) === currentDayKey);

  useEffect(() => { setDoneRows(new Set()); setSubTaskDone(new Set()); }, [resolvedActiveMemberId, currentDayKey]);

  const activeMember = members?.find(m => m.id === resolvedActiveMemberId);

  function toggleDone(ri) {
    setDoneRows(prev => { const n = new Set(prev); n.has(ri) ? n.delete(ri) : n.add(ri); return n; });
  }
  function toggleSubtask(ri, si) {
    const key = `${ri}-${si}`;
    setSubTaskDone(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  }

  if (!members?.length) return null;

  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden", background: C.surface }}>
      {/* Member tabs — only shown for 2+ members */}
      {!isSolo && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", padding: "12px 16px", borderBottom: `1px solid ${C.border}`, background: C.bg }}>
          {members.map((m, i) => (
            <button key={m.id} onClick={() => setActiveMemberId(m.id)} style={{
              padding: "6px 14px", borderRadius: 20,
              border: `2px solid ${memberColors[i % memberColors.length]}`,
              background: resolvedActiveMemberId === m.id ? memberColors[i % memberColors.length] : "transparent",
              color: resolvedActiveMemberId === m.id ? "#fff" : memberColors[i % memberColors.length],
              fontWeight: 700, fontSize: 12, cursor: "pointer",
            }}>
              {m.name} <span style={{ opacity: 0.8, fontWeight: 400 }}>· {m.projectRole?.title || m.targetRole || m.skillTag}</span>
            </button>
          ))}
        </div>
      )}

      {activeMember && (
        <div style={{ padding: "10px 16px", fontSize: 12, color: C.muted, borderBottom: `1px solid ${C.border}` }}>
          {isSolo ? (
            <>Your schedule · {memberDays.length} day{memberDays.length === 1 ? "" : "s"}</>
          ) : (
            <>Schedule for <strong style={{ color: C.text }}>{activeMember.name}</strong> · {memberDays.length} day{memberDays.length === 1 ? "" : "s"}</>
          )}
          {activeMember.targetRole ? <> · Role: <strong style={{ color: "#0369A1" }}>{activeMember.targetRole}</strong></> : null}
          {activeMember.targetCompany ? <> · Target: <strong style={{ color: "#7C3AED" }}>{activeMember.targetCompany}</strong></> : null}
        </div>
      )}

      {/* Week tabs */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", padding: "10px 16px" }}>
        {weeks.map(w => (
          <button key={w} onClick={() => { setActiveWeek(w); setActiveDayNum(null); }} style={{
            padding: "5px 14px", borderRadius: 20, border: `1.5px solid ${C.border}`,
            background: currentWeek === w ? C.primary : "transparent",
            color: currentWeek === w ? "#fff" : C.muted,
            fontWeight: 700, fontSize: 11, cursor: "pointer",
          }}>Week {w}</button>
        ))}
      </div>

      {/* Day tabs — use real dayName when available */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", padding: "0 16px 12px" }}>
        {daysThisWeek.map(({ d }) => {
          const dayKey = getDayKey(d);
          const label  = resolveDayLabel(d);
          const dayNum = d.day || d.dayNum;
          return (
            <button key={dayKey} onClick={() => setActiveDayNum(dayKey)} style={{
              padding: "6px 14px", borderRadius: 8,
              border: `1.5px solid ${currentDayKey === dayKey ? C.primary : C.border}`,
              background: currentDayKey === dayKey ? C.primary + "12" : "transparent",
              color: currentDayKey === dayKey ? C.primary : C.text,
              fontWeight: 700, fontSize: 11.5, cursor: "pointer",
            }}>
              {label} {dayNum ? <span style={{ fontWeight: 500, opacity: 0.7 }}>(Day {dayNum})</span> : null}
            </button>
          );
        })}
      </div>

      {!current && (
        <div style={{ padding: "24px 16px", textAlign: "center", color: C.muted, fontSize: 12.5 }}>
          No schedule data found for this member yet.
        </div>
      )}

      {current && (() => {
        const dayLabel = resolveDayLabel(current.d);
        const dayNum   = current.d.day || current.d.dayNum;
        return (
          <>
            {/* Day header bar */}
            <div style={{ padding: "10px 16px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
              background: TABLE.editBanner, borderTop: `1px solid ${C.border}` }}>
              <span style={{ fontWeight: 800, fontSize: 13, color: C.text }}>
                {dayLabel}{dayNum ? ` · Day ${dayNum}` : ""}
              </span>
              {current.d.dtPhase && (
                <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 6, background: "#A855F720", color: "#A855F7", fontWeight: 700 }}>
                  {current.d.dtPhase}
                </span>
              )}
              {/* Project Role (persistent) */}
              {current.a.projectRole && (
                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 99,
                  background: C.primary + "18", color: C.primary }}>
                  🏷️ {current.a.projectRole}
                </span>
              )}
              {/* Daily Duty (rotating driver) */}
              {current.a.dailyRole && (
                <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 99,
                  background: current.a.dailyRole.includes("Driving") ? "#059669" + "18" : "#6B728018",
                  color: current.a.dailyRole.includes("Driving") ? "#059669" : "#6B7280" }}>
                  {current.a.dailyRole.includes("Driving") ? "🚀 Driving today" : `🤝 ${current.a.dailyRole}`}
                </span>
              )}
              {/* Today's driver callout */}
              {current.d.driverName && (
                <span style={{ fontSize: 10, color: "#6B7280", marginLeft: "auto" }}>
                  Today's driver: <strong style={{ color: "#059669" }}>{current.d.driverName}</strong>
                </span>
              )}
            </div>

            {/* Timed schedule table — same visual format as the main daily plan */}
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 700 }}>
                <colgroup>
                  <col style={{ width: "6%" }} />
                  <col style={{ width: "16%" }} />
                  <col style={{ width: "21%" }} />
                  <col style={{ width: "57%" }} />
                </colgroup>
                <thead>
                  <tr style={{ background: TABLE.day.headBg }}>
                    {["✓", "🕐  Time", "🎯  Activity", "📋  Task / Content"].map((col, i) => (
                      <th key={i} style={{
                        padding: "10px 16px", textAlign: "left", color: "#fff",
                        fontWeight: 700, fontSize: 11, borderRight: `1px solid ${TABLE.day.headBorder}`,
                        textTransform: "uppercase", letterSpacing: "0.04em",
                      }}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(current.a.rows || []).map((row, ri) => {
                    const rs    = getRowStyle(row.activity);
                    const isDone = doneRows.has(ri);
                    return (
                      <tr key={ri} style={{ borderBottom: `1px solid ${C.border}`, background: isDone ? "#F0FDF4" : rs.bg, opacity: isDone ? 0.7 : 1 }}>
                        <td style={{ padding: "10px 8px", textAlign: "center", borderRight: `1px solid ${C.border}`, verticalAlign: "top" }}>
                          <input type="checkbox" checked={isDone} onChange={() => toggleDone(ri)}
                            title="Mark this slot done" style={{ width: 16, height: 16, cursor: "pointer", marginTop: 4 }} />
                        </td>
                        <td style={{ padding: "10px 16px", color: rs.accent, fontWeight: 700, fontSize: 12, borderRight: `1px solid ${C.border}`, verticalAlign: "top", whiteSpace: "nowrap" }}>
                          {formatTime12(row.time) || row.time}
                        </td>
                        <td style={{ padding: "10px 16px", fontWeight: 600, fontSize: 12.5, borderRight: `1px solid ${C.border}`, verticalAlign: "top", textDecoration: isDone ? "line-through" : "none" }}>
                          <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
                            <span style={{ width: 8, height: 8, borderRadius: "50%", background: rs.dot, flexShrink: 0, marginTop: 5 }} />
                            <span style={{ color: rs.accent }}>{row.activity}</span>
                            {rs.group && (
                              <span style={{ fontSize: 9, fontWeight: 800, padding: "1px 6px", borderRadius: 99,
                                background: "#1B4F7222", color: "#143B56", border: "1px solid #143B5640", flexShrink: 0 }}>
                                ALL MEMBERS
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: "10px 16px", color: C.text, fontSize: 12.5, lineHeight: 1.6, verticalAlign: "top", wordBreak: "break-word" }}>
                          {parseTaskLines(row.content).map((line, li) => {
                            if (!line.trackable) {
                              return line.stripped
                                ? <div key={li} style={{ whiteSpace: "pre-wrap" }}>{line.text}</div>
                                : <div key={li} style={{ height: 4 }} />;
                            }
                            const taskKey  = `${ri}-${line.subIndex}`;
                            const taskDone = subTaskDone.has(taskKey);
                            return (
                              <label key={li} style={{ display: "flex", alignItems: "flex-start", gap: 7, padding: "2px 0", cursor: "pointer" }}>
                                <input type="checkbox" checked={taskDone} onChange={() => toggleSubtask(ri, line.subIndex)}
                                  title="Mark this specific task done" style={{ width: 14, height: 14, cursor: "pointer", marginTop: 3, flexShrink: 0 }} />
                                <span style={{ whiteSpace: "pre-wrap", textDecoration: taskDone ? "line-through" : "none", color: taskDone ? C.muted : C.text }}>
                                  {line.stripped}
                                </span>
                              </label>
                            );
                          })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        );
      })()}
    </div>
  );
}
